/**
 * Partner referral capture.
 *
 * A partner hands a prospect a link like `https://app.aelo.cloud/?ref=CODE`.
 * We capture the code on app load and persist it to localStorage so it survives
 * Clerk's sign-up/redirect dance, then attach it to the onboarding request where
 * it lands on the new user + org. Free-form code (no managed partners list yet);
 * the charset guard keeps junk out of storage.
 *
 * Kept dependency-free (no logger/Sentry) so it's safe on the synchronous boot
 * path and trivial to test under jsdom.
 */

const STORAGE_KEY = 'aelo_referral_code';
const CODE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;

function isValidCode(value: string | null | undefined): value is string {
  return typeof value === 'string' && CODE_PATTERN.test(value);
}

/**
 * Read `?ref=` from the current URL and, if valid, persist it. No-op when the
 * param is absent or malformed, or when storage is unavailable (private mode).
 * Safe to call once on every app load.
 */
export function captureReferralCode(): void {
  try {
    const code = new URLSearchParams(window.location.search).get('ref');
    if (isValidCode(code)) {
      window.localStorage.setItem(STORAGE_KEY, code);
    }
  } catch {
    // Never break boot because referral capture failed.
  }
}

/**
 * Return the stored referral code, or undefined if none/invalid/unavailable.
 */
export function getStoredReferralCode(): string | undefined {
  try {
    const code = window.localStorage.getItem(STORAGE_KEY);
    return isValidCode(code) ? code : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Drop the stored code. Called after a successful onboarding so a shared browser
 * doesn't attribute the next account to the same partner.
 */
export function clearReferralCode(): void {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
